import { useCallback } from "react"
import { useDispatch } from "react-redux"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import useResource from "hooks/resources"
import { useAccountAlias } from "resources/account"
import { ResourceResult } from "helpers/data-statuses"
import { Uuid } from "helpers/types/uuid"
import type { LayoutConfigV1 } from "connected-components/dashboard/contracts/layouts"

// A layout row from GET /api/:account/dashboard/layouts. `config` is server-validated
// LayoutConfigV1 (see contracts/layouts ADR 0003 — the client trusts it).
export interface Layout {
  id: Uuid
  name: string
  version: number
  config: LayoutConfigV1
}

export function useLayouts(): ResourceResult<Layout[]> {
  const account = useAccountAlias()

  return useResource<Layout[]>({
    resource: "layouts",
    url: () => account ? `/api/${account}/dashboard/layouts` : null,
  }, "layouts")
}

export function useLayout(layoutId: string | null): ResourceResult<Layout> {
  const account = useAccountAlias()

  return useResource<Layout>({
    resource: "layout",
    url: () => account && layoutId ? `/api/${account}/dashboard/layouts/${layoutId}` : null,
  }, "layout")
}

// Imperative GET-by-id, for the CAS retry path: after a 409 the caller needs the layout's
// CURRENT version (the cached useLayout result is stale by definition).
export function useFetchLayout() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (layoutId: string): Promise<Layout> =>
      new Promise<Layout>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.get(`/api/${account}/dashboard/layouts/${layoutId}`, resolve, reject))
      }),
    [account, dispatch],
  )
}

// Creates a user-owned layout (server assigns id + initial version). Always mints ownerType "user":
// the APP-3155 lazy migration turns the creator's own dashboard into a private layout.
export function useCreateLayout() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (name: string, config: LayoutConfigV1): Promise<Layout> =>
      new Promise<Layout>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.post(`/api/${account}/dashboard/layouts`, { ownerType: "user", name, config }, resolve, reject))
      }),
    [account, dispatch],
  )
}

// CAS write: `expectedVersion` lets the server reject a stale overwrite (409).
export function useSaveLayoutConfig() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (layoutId: string, config: LayoutConfigV1, expectedVersion: number) =>
      new Promise((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.put(`/api/${account}/dashboard/layouts/${layoutId}`, { config, expectedVersion }, resolve, reject))
      }),
    [account, dispatch],
  )
}
